// ═══════════════════════════════════════════════════════════════
// recordScope.js: which chats and tickets the signed-in user may load.
// Mirrors the help-center database rules: support reads everything,
// an org admin queries their own organization (never tenant-public),
// everyone else queries their own records by userId.
// ═══════════════════════════════════════════════════════════════

const PUBLIC_TENANT_ID = 'tenant-public';

// Claims from the adapter win over the host user object because the rules
// check claims. Adapters without getAuthScope fall back to the user prop.
async function resolveRecordScope(storage, user) {
  let claims = null;
  if (storage?.getAuthScope) {
    try { claims = await storage.getAuthScope(); } catch (e) { claims = null; }
  }
  return {
    userId: claims?.userId || user?.id || null,
    tenantId: claims?.tenantId || user?.tenantId || null,
    role: claims?.role || user?.role || null,
  };
}

// Own records only.
function ownerQuery(scope) {
  return { child: 'userId', value: scope.userId };
}

// Agent dashboard: null means the full collection (support only).
function agentQuery(scope) {
  if (scope.role === 'support') return null;
  if (scope.role === 'admin' && scope.tenantId && scope.tenantId !== PUBLIC_TENANT_ID) {
    return { child: 'tenantId', value: scope.tenantId };
  }
  return ownerQuery(scope);
}

// Agent presence is grouped by organization. Super admins sit in a shared group
// every organization can see; org admins only in their own (never tenant-public).
const ALL_ORGS_BUCKET = '_all';

function presenceBucket(scope) {
  if (scope.role === 'support') return ALL_ORGS_BUCKET;
  if (scope.role === 'admin' && scope.tenantId && scope.tenantId !== PUBLIC_TENANT_ID) return scope.tenantId;
  return null;
}

// Groups a user may watch. null means every group (support only).
function presenceWatchBuckets(scope) {
  if (scope.role === 'support') return null;
  return scope.tenantId && scope.tenantId !== PUBLIC_TENANT_ID ? [ALL_ORGS_BUCKET, scope.tenantId] : [ALL_ORGS_BUCKET];
}

function listRecords(storage, path, query) {
  if (!query) return storage.list(path);
  if (storage.listByChild) return storage.listByChild(path, query.child, query.value);
  return storage.list(path).then(all => (all || []).filter(r => r[query.child] === query.value));
}

function subscribeRecords(storage, path, query, callback) {
  if (!query) return storage.subscribe(path, callback);
  if (storage.subscribeByChild) return storage.subscribeByChild(path, query.child, query.value, callback);
  return storage.subscribe(path, (data) => {
    if (!data) { callback(null); return; }
    const matches = Object.fromEntries(Object.entries(data).filter(([, r]) => r?.[query.child] === query.value));
    callback(Object.keys(matches).length ? matches : null);
  });
}

export { resolveRecordScope, ownerQuery, agentQuery, presenceBucket, presenceWatchBuckets, listRecords, subscribeRecords };
